import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import type { InboxChannel } from '../../types/inbox';
import { failingPlatforms, failingPlatformsForChannel } from '../../lib/connectionHealth';
import { getAccountConnectionHealth } from './api';
import { healthReconnectHref, PLATFORM_LABEL } from './connectionHealthView';

// Per-account "Connection expired — reconnect" indicator. Renders NOTHING when
// the account's connection is healthy (the common case), so it's cheap to drop
// onto any account-scoped surface (account detail API Connections, community
// inbox thread). Reads the durable connectionHealth doc — a dead token here
// means comments/reviews/DMs (+ reporting) have silently stopped.
//
// PLATFORM SCOPING (Part E, 2026-07-19): the health doc is ACCOUNT-level, so an
// unscoped render put a Google Business failure on an Instagram thread. Pass
// `channel` on any thread-level surface to narrow it to the platform(s) that
// thread actually depends on; omit it for the account-wide surface (the account
// detail page), which legitimately reports every platform.

export function ConnectionExpiredChip({
  accountId,
  channel,
  className,
  compact = false,
}: {
  accountId: string;
  // Scope to one thread's platform. Omitted = account-wide (all platforms).
  channel?: InboxChannel;
  className?: string;
  // compact = just the badge (inbox header); full = badge + detail + Reconnect.
  compact?: boolean;
}) {
  const q = useQuery({
    queryKey: ['connection-health', 'account', accountId],
    queryFn: () => getAccountConnectionHealth(accountId),
    staleTime: 60_000,
  });

  const failing = channel
    ? failingPlatformsForChannel(q.data?.health, channel)
    : failingPlatforms(q.data?.health);
  if (failing.length === 0) return null;

  const labels = failing.map((p) => PLATFORM_LABEL[p]).join(', ');
  const href = healthReconnectHref({ accountId, failing: failing.map((platform) => ({ platform })) });

  if (compact) {
    return (
      <Badge variant="danger" className={`gap-1 ${className ?? ''}`} title={`Reconnect: ${labels}`}>
        <AlertTriangle className="h-3 w-3" />
        Connection expired
      </Badge>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 ${className ?? ''}`}
    >
      <AlertTriangle className="h-4 w-4 text-rose-600" />
      <span className="text-sm text-rose-800">
        <span className="font-medium">Connection expired</span> — {labels} stopped fetching. Reconnect
        to resume.
      </span>
      <Button asChild size="sm" variant="outline" className="ml-auto h-7">
        <Link to={href}>Reconnect</Link>
      </Button>
    </div>
  );
}
